// DashboardXL - Sprint 51.
// Cableado a los endpoints premium del backend (Sprint 50 Fase 2).
// Lee /api/dashboard_premium/kpis via useDashboardMetrics y muestra:
// - 5 KPIs de ventas (hoy, mes, clientes, productos, ticket promedio)
// - Sparkline de ventas ultimos 7 dias
// - Accesos rapidos
//
// Regla: no se modifica backend.
import { useEffect, useMemo } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useDashboardMetrics from './hooks/useDashboardMetrics';
import XLKpiCard from './components/XLKpiCard';
import XLSparkArea from './components/XLSparkArea';
import QuickActions from './components/QuickActions';
import XLBadge from '../../components/xl/XLBadge';
import { fmtMoney } from '../../utils/money';
import { fmtCopCompact, fmtInt, fmtFechaCorta } from './utils';

const CSS_ID = 'xl-dashboard-css';
const CSS = `
.xl-dash { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-dash__head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap;
}
.xl-dash__title { font-size: 22px; font-weight: 600; margin: 0; color: var(--xl-color-text) }
.xl-dash__subtitle { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }

.xl-dash__btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 9px 14px;
  border-radius: var(--xl-radius-md);
  border: 1px solid var(--xl-color-border);
  background: var(--xl-color-bg);
  color: var(--xl-color-text-secondary);
  cursor: pointer;
  transition: background 120ms cubic-bezier(0,0,0.2,1),
              border-color 120ms cubic-bezier(0,0,0.2,1);
}
.xl-dash__btn:hover { background: var(--xl-color-surface-alt); color: var(--xl-color-text) }
.xl-dash__btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-dash__btn:disabled { opacity: 0.5; cursor: not-allowed }

.xl-dash__banner {
  display: flex; align-items: flex-start; gap: 10px;
  background: rgba(217,119,6,0.08);
  border: 1px solid rgba(217,119,6,0.28);
  color: var(--xl-color-warning);
  border-radius: var(--xl-radius-md);
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.45;
}
.xl-dash__banner--error {
  background: rgba(220,38,38,0.08);
  border-color: rgba(220,38,38,0.28);
  color: var(--xl-color-danger);
}
.xl-dash__banner strong { font-weight: 600 }
.xl-dash__banner-list { margin: 4px 0 0 18px; padding: 0 }

.xl-dash__kpis {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}
@media (max-width: 1100px) { .xl-dash__kpis { grid-template-columns: repeat(2, 1fr) } }
@media (max-width: 560px)  { .xl-dash__kpis { grid-template-columns: 1fr } }

.xl-dash__grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
}
@media (max-width: 960px) { .xl-dash__grid { grid-template-columns: 1fr } }

.xl-dash__card {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  overflow: hidden;
}
.xl-dash__card-hd {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--xl-color-border);
}
.xl-dash__card-hd-lt { display: flex; align-items: center; gap: 10px; min-width: 0 }
.xl-dash__card-title {
  font-size: 13px; font-weight: 600; color: var(--xl-color-text); margin: 0;
}
.xl-dash__card-body { padding: 12px 16px 16px }
.xl-dash__last-value {
  font-family: var(--xl-font-mono); font-size: 12px;
  color: var(--xl-color-text-secondary);
}

/* Sprint 54 F2 - bottom grid (top productos / top clientes / ventas por mes) */
.xl-dash__grid-bottom {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
}
@media (max-width: 1100px) { .xl-dash__grid-bottom { grid-template-columns: 1fr 1fr } }
@media (max-width: 700px)  { .xl-dash__grid-bottom { grid-template-columns: 1fr } }

.xl-dash__list { list-style: none; padding: 0; margin: 0 }
.xl-dash__list-item {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 8px 0;
  border-bottom: 1px solid var(--xl-color-border);
}
.xl-dash__list-item:last-child { border-bottom: 0 }
.xl-dash__list-main { min-width: 0; flex: 1 }
.xl-dash__list-name {
  font-size: 13px; color: var(--xl-color-text);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.xl-dash__list-sub {
  font-size: 11px; color: var(--xl-color-text-secondary);
  margin-top: 2px;
}
.xl-dash__list-value {
  font-family: var(--xl-font-mono); font-size: 12px;
  color: var(--xl-color-text); white-space: nowrap;
}

.xl-dash__bar-row { display: flex; align-items: center; gap: 10px; padding: 6px 0 }
.xl-dash__bar-label {
  font-size: 11px; color: var(--xl-color-text-secondary);
  width: 56px; font-family: var(--xl-font-mono); flex-shrink: 0;
}
.xl-dash__bar-track {
  flex: 1; height: 6px; background: var(--xl-color-surface-alt);
  border-radius: 3px; overflow: hidden;
}
.xl-dash__bar-fill {
  height: 100%; background: var(--xl-color-info); border-radius: 3px;
  transition: width 200ms cubic-bezier(0,0,0.2,1);
}
.xl-dash__bar-value {
  font-size: 11px; font-family: var(--xl-font-mono);
  width: 90px; text-align: right; color: var(--xl-color-text); flex-shrink: 0;
}

.xl-dash__empty {
  font-size: 12px; color: var(--xl-color-text-secondary);
  padding: 12px 0; text-align: center; margin: 0;
}
`;

export default function DashboardXL() {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const { kpis, topProductos, topClientes, ventasPorMes, loading, err, reload } = useDashboardMetrics();

  // Adapter Sprint 51: el sparkline (XLSparkArea + delta + last)
  // sigue esperando { fecha, total_cop }. kpis.ventas_por_dia viene
  // como { fecha, total }. Mapeo una sola vez.
  const serie = useMemo(
    () => (kpis && Array.isArray(kpis.ventas_por_dia))
      ? kpis.ventas_por_dia.map((d) => ({ fecha: d.fecha, total_cop: d.total }))
      : [],
    [kpis]
  );

  const delta = useMemo(() => {
    if (!serie || serie.length < 2) return null;
    const first = Number(serie[0].total_cop || 0);
    const last  = Number(serie[serie.length - 1].total_cop || 0);
    const d = last - first;
    return { value: d, abs: Math.abs(d), pos: d >= 0 };
  }, [serie]);

  const last = serie && serie.length > 0
    ? Number(serie[serie.length - 1].total_cop || 0)
    : 0;

  const today = new Date().toLocaleDateString('es-CO');

  return (
    <div className="xl-dash">
      <header className="xl-dash__head">
        <div>
          <h2 className="xl-dash__title">Dashboard</h2>
          <p className="xl-dash__subtitle">Resumen operativo · {today}</p>
        </div>
        <button
          type="button"
          className="xl-dash__btn"
          onClick={reload}
          disabled={loading}
        >
          {loading ? 'Cargando\u2026' : 'Actualizar'}
        </button>
      </header>

      {err && (
        <div className="xl-dash__banner xl-dash__banner--error" role="alert">
          <strong>Error al cargar el dashboard:</strong>
          <span>{err}</span>
        </div>
      )}

      <section className="xl-dash__kpis" aria-label="Indicadores clave">
        <XLKpiCard
          label="Ventas hoy"
          value={fmtMoney(kpis && kpis.total_ventas_hoy, 'COP')}
          hint={today}
          accent="success"
          loading={loading}
        />
        <XLKpiCard
          label="Ventas del mes"
          value={fmtMoney(kpis && kpis.total_ventas_mes, 'COP')}
          hint="Acumulado mensual"
          accent="indigo"
          loading={loading}
        />
        <XLKpiCard
          label="Clientes"
          value={fmtInt(kpis && kpis.total_clientes)}
          hint="Total registrados"
          accent="info"
          loading={loading}
        />
        <XLKpiCard
          label="Productos"
          value={fmtInt(kpis && kpis.total_productos)}
          hint="Catalogo activo"
          accent="neutral"
          loading={loading}
        />
        <XLKpiCard
          label="Ticket promedio"
          value={fmtMoney(kpis && kpis.ticket_promedio, 'COP')}
          hint="Por factura"
          accent="warning"
          loading={loading}
        />
      </section>

      <section className="xl-dash__grid">
        <div className="xl-dash__card">
          <div className="xl-dash__card-hd">
            <div className="xl-dash__card-hd-lt">
              <h3 className="xl-dash__card-title">Ventas ultimos 7 dias</h3>
              <span className="xl-dash__last-value">{fmtMoney(last, 'COP')}</span>
              {delta && (
                <XLBadge variant={delta.pos ? 'success' : 'danger'}>
                  {(delta.pos ? '\u25B2 ' : '\u25BC ') + fmtCopCompact(delta.abs)}
                </XLBadge>
              )}
            </div>
            <XLBadge variant="info">7D</XLBadge>
          </div>
          <div className="xl-dash__card-body">
            <XLSparkArea
              serie={serie}
              ariaLabel="Ventas, ultimos 7 dias"
              formatValue={(v) => fmtMoney(v, 'COP')}
              formatCompact={fmtCopCompact}
              formatX={fmtFechaCorta}
              emptyMessage={loading ? 'Cargando serie\u2026' : 'Sin ventas en el periodo'}
            />
          </div>
        </div>

        <div className="xl-dash__card">
          <div className="xl-dash__card-hd">
            <div className="xl-dash__card-hd-lt">
              <h3 className="xl-dash__card-title">Accesos rapidos</h3>
            </div>
          </div>
          <div className="xl-dash__card-body">
            <QuickActions />
          </div>
        </div>
      </section>

      {/* Sprint 54 F2: top productos / top clientes / ventas por mes */}
      <section className="xl-dash__grid-bottom" aria-label="Detalle de ventas">
        <div className="xl-dash__card">
          <div className="xl-dash__card-hd">
            <div className="xl-dash__card-hd-lt">
              <h3 className="xl-dash__card-title">Top 5 productos</h3>
            </div>
            <XLBadge variant="neutral">{topProductos.length}</XLBadge>
          </div>
          <div className="xl-dash__card-body">
            {loading && topProductos.length === 0 ? (
              <p className="xl-dash__empty">Cargando…</p>
            ) : topProductos.length === 0 ? (
              <p className="xl-dash__empty">Sin facturas en el periodo</p>
            ) : (
              <ul className="xl-dash__list">
                {topProductos.map((p) => (
                  <li key={p.id} className="xl-dash__list-item">
                    <div className="xl-dash__list-main">
                      <div className="xl-dash__list-name">{p.name}</div>
                      <div className="xl-dash__list-sub">{p.code} · {fmtInt(p.cantidad)} u</div>
                    </div>
                    <span className="xl-dash__list-value">{fmtMoney(p.valor, 'COP')}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="xl-dash__card">
          <div className="xl-dash__card-hd">
            <div className="xl-dash__card-hd-lt">
              <h3 className="xl-dash__card-title">Top 5 clientes</h3>
            </div>
            <XLBadge variant="neutral">{topClientes.length}</XLBadge>
          </div>
          <div className="xl-dash__card-body">
            {loading && topClientes.length === 0 ? (
              <p className="xl-dash__empty">Cargando…</p>
            ) : topClientes.length === 0 ? (
              <p className="xl-dash__empty">Sin facturas en el periodo</p>
            ) : (
              <ul className="xl-dash__list">
                {topClientes.map((c) => (
                  <li key={c.id} className="xl-dash__list-item">
                    <div className="xl-dash__list-main">
                      <div className="xl-dash__list-name">{c.name}</div>
                      <div className="xl-dash__list-sub">{c.facturas} factura{c.facturas === 1 ? '' : 's'}</div>
                    </div>
                    <span className="xl-dash__list-value">{fmtMoney(c.total, 'COP')}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="xl-dash__card">
          <div className="xl-dash__card-hd">
            <div className="xl-dash__card-hd-lt">
              <h3 className="xl-dash__card-title">Ventas por mes</h3>
            </div>
            <XLBadge variant="info">12M</XLBadge>
          </div>
          <div className="xl-dash__card-body">
            {loading && ventasPorMes.length === 0 ? (
              <p className="xl-dash__empty">Cargando…</p>
            ) : ventasPorMes.length === 0 ? (
              <p className="xl-dash__empty">Sin datos</p>
            ) : (() => {
              const max = Math.max(1, ...ventasPorMes.map((m) => Number(m.total) || 0));
              return ventasPorMes.map((m) => {
                const pct = ((Number(m.total) || 0) / max) * 100;
                return (
                  <div key={m.periodo} className="xl-dash__bar-row">
                    <span className="xl-dash__bar-label">{m.periodo}</span>
                    <div className="xl-dash__bar-track">
                      <div className="xl-dash__bar-fill" style={{ width: pct + '%' }} />
                    </div>
                    <span className="xl-dash__bar-value">{fmtCopCompact(m.total)}</span>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>
    </div>
  );
}
